import { getCurrentScope, onScopeDispose, readonly, ref } from 'vue';

/**
 * Generic busy/alive gate for user-initiated async flows (pickers, uploads, etc.).
 *
 * - runGuarded(fn) — drops re-entrant calls while a flow is in-flight; re-throws errors.
 * - isBusy — readonly ref for UI disable state.
 * - isCancelled() — true after the owning scope has been disposed; suppress post-flow side effects.
 */
export const useFlowGuard = () => {
  const isBusy = ref(false);
  const isAlive = ref(true);

  if (getCurrentScope()) {
    onScopeDispose(() => {
      isAlive.value = false;
    });
  }

  const runGuarded = async (fn: () => Promise<void>): Promise<void> => {
    if (isBusy.value) {
      return;
    }
    isBusy.value = true;
    try {
      await fn();
    } finally {
      isBusy.value = false;
    }
  };

  const isCancelled = (): boolean => !isAlive.value;

  return { isBusy: readonly(isBusy), runGuarded, isCancelled };
};
